import LoadingIconComponent from 'components/Orders/iconComponent/LoadingIconComponent';
import { AmazonTotalPriceCell, MailboxPriceCell, TotalGananciaCell } from './SaleReportPriceCells';
import { formatAmazonWeight, formatReportAmount, formatReportDate, getTotalGananciaFooterClassName, isCanceledOrderStatus, UYU_TO_USD_RATE } from 'utils/reportSalesUtils';

const TABLE_COLUMNS = 15;

const isCanceledStatus = isCanceledOrderStatus;

const getStatusBadgeClassName = (value) => {
  if (isCanceledStatus(value)) {
    return 'bg-red-50 text-red-700';
  }

  return 'bg-gray-100 text-gray-700';
};

const StatusBadge = ({ value }) => (
  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full capitalize ${getStatusBadgeClassName(value)}`}>
    {value || '-'}
  </span>
);

const TableHeader = () => (
  <thead className='bg-gray-50 sticky top-0 z-10 shadow-sm'>
    <tr>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Producto
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Orden
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Fecha
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        SKU
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Peso Amazon (gs)
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Precio Amazon (USD)
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Precio casillero (USD)
      </th>
      <th className='px-4 py-3 text-left align-top'>
        <span className='block text-xs font-medium text-gray-500 uppercase tracking-wider'>
          Total USA (USD)
        </span>
        <span className='block mt-1 text-[10px] font-normal normal-case leading-tight text-gray-400'>
          Precio Amazon + Precio casillero
        </span>
      </th>
      <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Monto pagado ML
      </th>
      <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Comisión ML
      </th>
      <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Costo envío ML
      </th>
      <th className='px-4 py-3 text-right align-top'>
        <span className='block text-xs font-medium text-gray-500 uppercase tracking-wider'>
          Total Neto
        </span>
        <span className='block mt-1 text-[10px] font-normal normal-case leading-tight text-gray-400'>
          Monto pagado ML - (Comisión ML + Costo envío ML)
        </span>
      </th>
      <th className='px-4 py-3 text-right align-top'>
        <span className='block text-xs font-medium text-gray-500 uppercase tracking-wider'>
          Total Ganancia
        </span>
        <span className='block mt-1 text-[10px] font-normal normal-case leading-tight text-gray-400'>
          Total Neto - Total USA (cancelada: -Total USA)
        </span>
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Estado orden
      </th>
      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
        Estado envío
      </th>
    </tr>
  </thead>
);

const EmptyStateRow = ({ message }) => (
  <tr>
    <td colSpan={TABLE_COLUMNS} className='px-4 py-12 text-center text-sm text-gray-500'>
      {message}
    </td>
  </tr>
);

const LoadingBodyRow = () => (
  <tr>
    <td colSpan={TABLE_COLUMNS} className='px-4 py-12'>
      <div className='flex items-center justify-center text-sm text-gray-500'>
        <LoadingIconComponent />
        Cargando reporte...
      </div>
    </td>
  </tr>
);

const getRecordsLabel = (count) => {
  if (count === 1) return '1 registro encontrado';
  return `${count} registros encontrados`;
};

const getEmptyMessage = (hasSubmitted, loading) => {
  if (!hasSubmitted) {
    return 'Seleccioná un rango de fechas y presioná Generar para ver el reporte.';
  }
  if (loading) return 'Generando reporte...';
  return 'No hay registros para el rango de fechas seleccionado.';
};

const getHeaderLabel = (loading, hasSubmitted, totalItems) => {
  if (loading) return 'Generando reporte...';
  if (hasSubmitted && totalItems > 0) return getRecordsLabel(totalItems);
  return 'Reporte de ventas';
};

const SalesReportTable = ({
  rows,
  totals,
  totalsLoading,
  loading,
  hasSubmitted,
  pagination,
}) => {
  const totalPages = Math.max(
    1,
    Math.ceil(pagination.totalItems / pagination.pageSize)
  );
  const showPagination = !loading && pagination.totalItems > pagination.pageSize;
  const isPaginationLoading = pagination.loading && hasSubmitted && !loading;

  const emptyMessage = getEmptyMessage(hasSubmitted, loading);

  return (
    <div className='flex flex-col flex-1 min-h-0 bg-white rounded-lg shadow border border-gray-200 overflow-hidden'>
      <div className='flex-none px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2'>
        <p className='text-sm text-gray-600'>
          {getHeaderLabel(loading, hasSubmitted, pagination.totalItems)}
        </p>
        {hasSubmitted && !loading && pagination.totalItems > 0 && (
          <p className='text-xs text-gray-500'>
            {totalsLoading
              ? 'Calculando totales del período...'
              : 'Totales calculados sobre todos los registros del período'}
          </p>
        )}
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <table className='w-full min-w-[1400px] border-separate border-spacing-0'>
          <TableHeader />
          <tbody className='divide-y divide-gray-200 bg-white'>
            {loading && <LoadingBodyRow />}
            {!loading && rows.length > 0 && rows.map((row) => (
              <tr key={`${row.order_id}-${row.date_created}`} className='hover:bg-gray-50'>
                <td className='px-4 py-3 text-sm text-gray-900 max-w-xs'>
                  <span className='line-clamp-2' title={row.reason}>
                    {row.reason}
                  </span>
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-700'>
                  {row.order_id}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-500'>
                  {formatReportDate(row.date_created)}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-500'>
                  {row.seller_custom_field || '-'}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-500'>
                  {formatAmazonWeight(row.amazon_weight)}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-500'>
                  {row.amazon_price ? `${row.amazon_price} (${formatReportAmount(row.amazon_price * UYU_TO_USD_RATE)})` : '-'}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-500'>
                  <MailboxPriceCell
                    amazonPrice={row.amazon_price}
                    amazonWeight={row.amazon_weight}
                  />
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-500'>
                  <AmazonTotalPriceCell
                    amazonPrice={row.amazon_price}
                    amazonWeight={row.amazon_weight}
                  />
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium'>
                  {formatReportAmount(row.total_paid_amount)}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right'>
                  {formatReportAmount(row.sale_fee)}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right'>
                  {formatReportAmount(row.cost)}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                  {formatReportAmount(row.total)}
                </td>
                <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                  <TotalGananciaCell
                    amazonPrice={row.amazon_price}
                    amazonWeight={row.amazon_weight}
                    totalNeto={row.total}
                    statusOrder={row.statusOrder}
                  />
                </td>
                <td className='px-4 py-3 whitespace-nowrap'>
                  <StatusBadge value={row.statusOrder} />
                </td>
                <td className='px-4 py-3 whitespace-nowrap'>
                  <StatusBadge value={row.statusShipment} />
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <EmptyStateRow message={emptyMessage} />}
          </tbody>

          {hasSubmitted && !loading && pagination.totalItems > 0 && (totals || totalsLoading) && (
            <tfoot className='bg-gray-50 border-t border-gray-200 sticky bottom-0 z-10 shadow-[0_-1px_0_0_#e5e7eb]'>
              <tr>
                <td colSpan={7} className='px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-50'>
                  Totales ({pagination.totalItems})
                </td>
                {totalsLoading && !totals ? (
                  <td colSpan={6} className='px-4 py-3 text-sm text-gray-500 bg-gray-50'>
                    <div className='flex items-center justify-end'>
                      <LoadingIconComponent />
                      Cargando totales...
                    </div>
                  </td>
                ) : (
                  <>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-50'>
                      {totals.total_usa != null ? formatReportAmount(totals.total_usa) : '-'}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-50'>
                      {formatReportAmount(totals.total_paid_amount)}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-50'>
                      {formatReportAmount(totals.sale_fee)}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-semibold bg-gray-50'>
                      {formatReportAmount(totals.cost)}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-bold bg-gray-50'>
                      {formatReportAmount(totals.total)}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${getTotalGananciaFooterClassName(totals.total_ganancia)}`}>
                      {totals.total_ganancia != null ? formatReportAmount(totals.total_ganancia) : '-'}
                    </td>
                  </>
                )}
                <td colSpan={2} className='bg-gray-50' />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showPagination && (
        <div className='flex-none flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-white'>
          <p className='text-sm text-gray-600'>
            Mostrando{' '}
            {pagination.totalItems === 0
              ? 0
              : (pagination.currentPage - 1) * pagination.pageSize + 1}
            {pagination.totalItems > 0 && (
              <>
                –
                {Math.min(
                  pagination.currentPage * pagination.pageSize,
                  pagination.totalItems
                )}
              </>
            )}{' '}
            de {pagination.totalItems}
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              disabled={isPaginationLoading || pagination.currentPage <= 1}
              onClick={() => pagination.onPageChange(1)}
              className='px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Primera
            </button>
            <button
              type='button'
              disabled={isPaginationLoading || pagination.currentPage <= 1}
              onClick={() => pagination.onPageChange(Math.max(1, pagination.currentPage - 1))}
              className='px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Anterior
            </button>
            <span className='text-sm text-gray-700 tabular-nums px-1'>
              Página {pagination.currentPage} de {totalPages}
            </span>
            <button
              type='button'
              disabled={isPaginationLoading || pagination.currentPage >= totalPages}
              onClick={() => pagination.onPageChange(Math.min(totalPages, pagination.currentPage + 1))}
              className='px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Siguiente
            </button>
            <button
              type='button'
              disabled={isPaginationLoading || pagination.currentPage >= totalPages}
              onClick={() => pagination.onPageChange(totalPages)}
              className='px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Última
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesReportTable;
